import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CORE_ROLES, ROLE_METADATA, UserRole } from '@/components/authnz/types/rbac';
import type { PrototypeAction, PrototypeRow } from './types';
import { DiagnosticBlock, GrantChips, LifecycleBadge } from './parts';

export const VARIANT_B_NAME = 'Split inspector';

export function VariantB({
  rows,
  onAction,
}: {
  rows: PrototypeRow[];
  onAction: (action: PrototypeAction) => void;
  stepUpOk: boolean;
}) {
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState(rows[0]?.emailKey ?? '');
  const [assignRole, setAssignRole] = useState<UserRole>(UserRole.CLIENT);
  const [moveTo, setMoveTo] = useState('');

  const filtered = useMemo(
    () => rows.filter((row) => row.emailKey.includes(query.toLowerCase())),
    [rows, query],
  );
  const selected = rows.find((row) => row.emailKey === selectedKey) ?? filtered[0];

  return (
    <div className="grid min-h-[28rem] gap-3 md:grid-cols-[16rem_1fr]">
      <aside className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
        <Input
          placeholder="Filter emails"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <ul className="min-h-0 flex-1 space-y-1 overflow-auto">
          {filtered.map((row) => (
            <li key={row.emailKey}>
              <button
                type="button"
                className={`w-full rounded-md px-2 py-2 text-left text-sm ${
                  selected?.emailKey === row.emailKey
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-muted'
                }`}
                onClick={() => setSelectedKey(row.emailKey)}
              >
                <span className="block truncate font-medium">{row.emailKey}</span>
                <LifecycleBadge row={row} />
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        {selected ? (
          <>
            <header className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">{selected.emailKey}</h2>
              <LifecycleBadge row={selected} />
            </header>
            <div>
              <h3 className="mb-2 text-sm font-medium">Grants</h3>
              <GrantChips
                grants={selected.grants}
                onRevokeAdmin={(role) =>
                  onAction({ type: 'revoke-admin', emailKey: selected.emailKey, role })
                }
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <select
                className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                value={assignRole}
                onChange={(event) => setAssignRole(event.target.value as UserRole)}
              >
                {CORE_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_METADATA[role].displayName}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  onAction({
                    type: 'assign',
                    emailKey: selected.emailKey,
                    role: assignRole,
                  })
                }
              >
                Assign grant
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onAction({ type: 'copy-link', emailKey: selected.emailKey })}
              >
                Copy link (step-up)
              </Button>
              {selected.lifecycle === 'deleted' ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onAction({ type: 'restore', emailKey: selected.emailKey })}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={() => onAction({ type: 'delete', emailKey: selected.emailKey })}
                >
                  Delete
                </Button>
              )}
            </div>
            <div className="space-y-2 rounded-md border border-dashed border-border p-3">
              <p className="text-sm font-medium">Move grants to a new email</p>
              <p className="text-xs text-muted-foreground">
                Ticket 02 removed casual email edit. This variant tests a real move action.
              </p>
              <div className="flex flex-wrap gap-2">
                <Input
                  placeholder="new@clinic.com"
                  value={moveTo}
                  onChange={(event) => setMoveTo(event.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (moveTo.trim()) {
                      onAction({
                        type: 'move-email',
                        from: selected.emailKey,
                        to: moveTo.trim().toLowerCase(),
                      });
                      setSelectedKey(moveTo.trim().toLowerCase());
                      setMoveTo('');
                    }
                  }}
                >
                  Move
                </Button>
              </div>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="mb-2 text-sm font-medium">Activity (placeholder)</p>
              <p className="text-sm text-muted-foreground">
                adminActions drawer — fog. Decide if this effort ships it.
              </p>
            </div>
            <DiagnosticBlock row={selected} />
          </>
        ) : (
          <p className="text-muted-foreground">Select an email.</p>
        )}
      </section>
    </div>
  );
}
